import React from 'react';
import Card from '../../components/Card/Card';
import GqlQueryWrapper from '../../components/GqlQueryWrapper/GqlQueryWrapper';
import Table from '../../components/Table/Table';
import TablePreRender from '../../components/Table/Table.prerender';
import ErrorMessageNewFund from '../../components/ErrorMessages/ErrorMessageNewFund';
import { CARD_NAMES } from '../../utils/constants';
import {
    averageAnnualTableCardUtil,
    averageAnnualTableCardFeesExpensesUtil
} from './util';

import {
    showExpenseWaiverDisclaimer,
    showTackedFootnotes
} from '../../utils/fund-info';

import styles from './AverageAnnualTable.css';
import {
    AVERAGE_ANNUAL_TOTAL_RETURNS,
    AVERAGE_ANNUAL_TOTAL_RETURNS_FEES_EXPENSES
} from './queries';

const AverageAnnualTableCard = ({ bond, iClass, moneyMarket, ticker }) => {
    // don't show if it's a bond, money market
    // or one of the specified tickers
    const showPerfDisclaimer = !(
        bond ||
        moneyMarket ||
        ['RPBAX'].includes(ticker)
    );

    return (
        <div className="section-container">
            <Card>
                <GqlQueryWrapper
                    query={AVERAGE_ANNUAL_TOTAL_RETURNS}
                    variables={{ ticker }}
                    // @todo extract titles from the table component
                    // so we don't have to include this conditional
                    // logic in the util
                    dataFunc={averageAnnualTableCardUtil(iClass, ticker)}
                    placeholder={() => <TablePreRender />}
                >
                    {({
                        data: {
                            data,
                            headers,
                            hasTacked,
                            topValueOver40,
                            quarterlyAsOfDate
                        }
                    }) =>
                        !quarterlyAsOfDate ? (
                            <ErrorMessageNewFund
                                cardName={CARD_NAMES.AVERAGE_ANNUAL_CHART}
                            />
                        ) : (
                            <React.Fragment>
                                <div
                                    className="table-container"
                                    id="average-annual-table"
                                >
                                    <Table data={{ data, headers }} />
                                </div>
                                <div
                                    className="table"
                                    id="average-annual-supplement"
                                >
                                    <GqlQueryWrapper
                                        query={
                                            AVERAGE_ANNUAL_TOTAL_RETURNS_FEES_EXPENSES
                                        }
                                        variables={{ ticker }}
                                        dataFunc={averageAnnualTableCardFeesExpensesUtil(
                                            ticker
                                        )}
                                        placeholder={() => <TablePreRender />}
                                    >
                                        {({
                                            data: {
                                                data: feesExpensesData,
                                                transactionFee,
                                                activeMoneyMarketFundMessages,
                                                bottomValueOver40
                                            }
                                        }) => (
                                            <React.Fragment>
                                                <Table
                                                    rowBars
                                                    data={{
                                                        data: feesExpensesData
                                                    }}
                                                />
                                                {showPerfDisclaimer && (
                                                    <div className="current-performance">
                                                        Current performance may
                                                        be lower or higher than
                                                        the quoted past
                                                        performance, which
                                                        cannot guarantee future
                                                        results. Share price,
                                                        principal value, and
                                                        return will vary and you
                                                        may have a gain or loss
                                                        when you sell your
                                                        shares.
                                                    </div>
                                                )}
                                                {bond && (
                                                    <div className="current-performance">
                                                        Current performance may
                                                        be lower or higher than
                                                        the quoted past
                                                        performance, which
                                                        cannot guarantee future
                                                        results. Share price,
                                                        principal value, yield
                                                        and return will vary and
                                                        you may have a gain or
                                                        loss when you sell your
                                                        shares.
                                                    </div>
                                                )}
                                                {ticker === 'PRIGX' && (
                                                    <div className="performance-shown">
                                                        The performance for the
                                                        periods shown reflects
                                                        the performance of the
                                                        fund while it was named
                                                        the T. Rowe Price
                                                        Institutional Global
                                                        Value Equity Fund.
                                                        Effective March 1, 2020,
                                                        the T. Rowe Price
                                                        Institutional Global
                                                        Value Equity Fund was
                                                        renamed the T. Rowe
                                                        Price Global Value
                                                        Equity Fund.
                                                    </div>
                                                )}
                                                {ticker === 'TILCX' && (
                                                    <div className="performance-shown">
                                                        The Institutional Large
                                                        Cap Value Fund changes
                                                        its name to Large Cap
                                                        Value Fund and
                                                        designated all
                                                        outstanding shares as I
                                                        Class as of May 1, 2020.
                                                        Performance shown prior
                                                        to May 1, 2020, reflects
                                                        the performance of the
                                                        fund while it was
                                                        structured as the T.
                                                        Rowe Price Institutional
                                                        Large Cap Value Fund.
                                                    </div>
                                                )}
                                                {ticker === 'TRLGX' && (
                                                    <div className="performance-shown">
                                                        The Institutional
                                                        Large-Cap Growth Fund
                                                        changed its name to
                                                        Large-Cap Growth Fund -
                                                        I Class as of May 1,
                                                        2020. Performance shown
                                                        prior to May 1, 2020,
                                                        reflects the performance
                                                        of the fund while it was
                                                        structured as the T.
                                                        Rowe Price Institutional
                                                        Large-Cap Growth Fund.
                                                    </div>
                                                )}
                                                {moneyMarket && (
                                                    <div className="current-performance">
                                                        Current performance may
                                                        be lower or higher than
                                                        the quoted past
                                                        performance, which
                                                        cannot guarantee future
                                                        results. Return and
                                                        yield will vary.
                                                    </div>
                                                )}
                                                {(ticker === 'OTCFX' ||
                                                    ticker === 'RPBAX') && (
                                                    <div className="current-performance">
                                                        Total return information
                                                        before August 31, 1992
                                                        reflects performance by
                                                        managers other than T.
                                                        Rowe Price.
                                                    </div>
                                                )}
                                                {(bottomValueOver40 ||
                                                    topValueOver40) && (
                                                    <div className="avg-annual-return">
                                                        <sup>†</sup> Investors
                                                        should note that the
                                                        fund’s short-term
                                                        performance is highly
                                                        unusual and unlikely to
                                                        be sustained.{' '}
                                                    </div>
                                                )}
                                                {ticker === 'TUHYX' && (
                                                    <div className="avg-annual-return">
                                                        The Fund commenced
                                                        operations on May 19,
                                                        2017. Performance prior
                                                        to that date reflects
                                                        the performance of the
                                                        Class A of the
                                                        Predecessor Fund.
                                                        Predecessor Fund
                                                        performance reflects its
                                                        actual operating expense
                                                        at the time and was not
                                                        adjusted to reflect the
                                                        impact of the Fund{`'`}s
                                                        current fees. Since
                                                        inception returns are
                                                        shown from the inception
                                                        date of the Predecessor
                                                        Fund, April 30, 2013.
                                                    </div>
                                                )}
                                                {moneyMarket ? (
                                                    <div className="avg-annual-return">
                                                        <sup>1</sup>Average
                                                        annual total return
                                                        figures include
                                                        reinvested dividends and
                                                        capital gain
                                                        distributions, if any.
                                                        Returns for periods less
                                                        than one year are not
                                                        annualized. For funds
                                                        less than one year old,
                                                        the Since Inception
                                                        return figure is not
                                                        annualized and
                                                        represents an aggregate
                                                        total return. The yield
                                                        quotation more closely
                                                        reflects the current
                                                        earnings of the money
                                                        market fund than the
                                                        total return.
                                                    </div>
                                                ) : (
                                                    <div className="avg-annual-return">
                                                        <sup>1</sup>Average
                                                        annual total return
                                                        figures include changes
                                                        in principal value,
                                                        reinvested dividends,
                                                        and capital gain
                                                        distributions. Returns
                                                        for periods less than
                                                        one year are not
                                                        annualized. For funds
                                                        less than one year old,
                                                        the Since Inception
                                                        return figure is not
                                                        annualized and
                                                        represents an aggregate
                                                        total return.
                                                    </div>
                                                )}
                                                {showExpenseWaiverDisclaimer(
                                                    ticker
                                                ) && (
                                                    <div className="avg-annual-return">
                                                        * T. Rowe Price
                                                        Associates, Inc., has
                                                        contractually agreed to
                                                        permanently waive any
                                                        expenses that would
                                                        cause the class&apos;
                                                        ratio of expenses to
                                                        average daily net assets
                                                        to exceed the net
                                                        expense ratio listed
                                                        above. This waiver may
                                                        only be terminated by
                                                        approval of the
                                                        fund&apos;s
                                                        shareholders. Fees
                                                        waived and expenses paid
                                                        under the fund&apos;s
                                                        contractual expense
                                                        limitation arrangement
                                                        are subject to
                                                        reimbursement to T. Rowe
                                                        Price Associates by the
                                                        fund whenever the
                                                        fund&apos;s expense
                                                        ratio is below a certain
                                                        level. See the
                                                        prospectus for details.
                                                    </div>
                                                )}
                                                {hasTacked &&
                                                    showTackedFootnotes(
                                                        iClass
                                                    ) && (
                                                        <div className="avg-annual-return">
                                                            <sup>2</sup>The T.
                                                            Rowe Price Funds - I
                                                            Class share the
                                                            portfolio of an
                                                            existing fund (the
                                                            original share class
                                                            of the fund is
                                                            referred to as the
                                                            “investor class”).
                                                            The total return
                                                            figures for I Class
                                                            shares have been
                                                            calculated using the
                                                            performance data of
                                                            the investor class
                                                            up to the inception
                                                            date of the I Class
                                                            (shown in the
                                                            investment
                                                            objective, strategy,
                                                            and risks section)
                                                            and the actual
                                                            performance results
                                                            of the I Class since
                                                            that date. Because
                                                            the I Classes are
                                                            expected to have
                                                            lower expenses than
                                                            the Investor
                                                            Classes, the I Class
                                                            performance, had it
                                                            existed over the
                                                            periods shown, would
                                                            have been higher.
                                                            The inception date
                                                            shown in the returns
                                                            table above is the
                                                            date on which the
                                                            investor class
                                                            started operations
                                                        </div>
                                                    )}
                                                {[
                                                    'PPIPX',
                                                    'PRCIX',
                                                    'PRSIX',
                                                    'PRSNX',
                                                    'PRXEX',
                                                    'PTKIX',
                                                    'PTTFX',
                                                    'RBAIX',
                                                    'RPBAX',
                                                    'TGIPX',
                                                    'TPPAX',
                                                    'TRPBX',
                                                    'TRSGX',
                                                    'TMSSX',
                                                    'TTOIX'
                                                ].includes(ticker) && (
                                                    <div className="avg-annual-return">
                                                        T.&nbsp;Rowe&nbsp;Price
                                                        Associates, Inc. is
                                                        required to permanently
                                                        waive a portion of its
                                                        management fee charged
                                                        to the fund in an amount
                                                        sufficient to fully
                                                        offset any acquired fund
                                                        fees and expenses
                                                        related to investments
                                                        in other
                                                        T.&nbsp;Rowe&nbsp;Price
                                                        mutual funds. The amount
                                                        of the waiver will vary
                                                        each fiscal year in
                                                        proportion to the amount
                                                        invested in other
                                                        T.&nbsp;Rowe&nbsp;Price
                                                        mutual funds.
                                                    </div>
                                                )}
                                                {(ticker === 'TGAFX' ||
                                                    ticker === 'RPGAX') && (
                                                    <div className="avg-annual-return">
                                                        As a result of other
                                                        class&apos; expense
                                                        limitations,
                                                        T.&nbsp;Rowe&nbsp;Price
                                                        Associates, Inc. waived
                                                        fund-level expenses
                                                        ratably across all
                                                        classes. The fund&apos;s
                                                        net expense ratio
                                                        excludes expenses
                                                        permanently waived as a
                                                        result of investments in
                                                        other
                                                        T.&nbsp;Rowe&nbsp;Price
                                                        mutual funds and
                                                        expenses that were
                                                        waived or borne by
                                                        T.&nbsp;Rowe&nbsp;Price
                                                        pursuant to the
                                                        fund&apos;s expense
                                                        limitation, but includes
                                                        expenses associated with
                                                        investments in
                                                        non-T.&nbsp;Rowe&nbsp;Price
                                                        Investment companies.
                                                    </div>
                                                )}
                                                {ticker === 'VEIEX' && (
                                                    <div className="avg-annual-return">
                                                        This fund has a
                                                        transaction fee of .5%
                                                        of the principal amount
                                                        of the transaction,
                                                        which is in addition to
                                                        the $35.00 transaction
                                                        fee charged by T. Rowe
                                                        Price.
                                                    </div>
                                                )}
                                                {Boolean(transactionFee) && (
                                                    <div className="avg-annual-return">
                                                        T.&nbsp;Rowe&nbsp;Price
                                                        Associates, Inc. is
                                                        required to permanently
                                                        waive a portion of its
                                                        management fee charged
                                                        to the fund in an amount
                                                        sufficient to fully
                                                        offset any acquired fund
                                                        fees and expenses
                                                        related to investments
                                                        in other
                                                        T.&nbsp;Rowe&nbsp;Price
                                                        mutual funds. The amount
                                                        of the waiver will vary
                                                        each fiscal year in
                                                        proportion to the amount
                                                        invested in other
                                                        T.&nbsp;Rowe&nbsp;Price
                                                        mutual funds.
                                                    </div>
                                                )}
                                                {moneyMarket &&
                                                    Boolean(
                                                        activeMoneyMarketFundMessages.length
                                                    ) && (
                                                        <div className="avg-annual-return">
                                                            <sup>&#94;</sup> The
                                                            fund generally does
                                                            not charge a
                                                            redemption fee but
                                                            may impose a
                                                            liquidity fee upon
                                                            the sale of your
                                                            shares or may
                                                            temporarily suspend
                                                            your ability to see
                                                            shares if the
                                                            Fund&apos;s
                                                            liquidity falls
                                                            below required
                                                            minimums because of
                                                            market conditions or
                                                            other factors.
                                                        </div>
                                                    )}
                                            </React.Fragment>
                                        )}
                                    </GqlQueryWrapper>
                                </div>
                            </React.Fragment>
                        )
                    }
                </GqlQueryWrapper>
            </Card>
            <style jsx>{styles}</style>
        </div>
    );
};

export default AverageAnnualTableCard;
